import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Alert, Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useMemo, useRef } from "react";
import { getEnvVar } from "../../../../../../utils/get-env";

const stripePublishableKey = getEnvVar("REACT_APP_STRIPE_PUBLISHABLE_KEY") ?? process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ?? "";
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

export interface PortingStripeCheckoutDialogProps {
  open: boolean;
  clientSecret: string | null;
  onClose: () => void;
  onComplete: () => void;
}

export function PortingStripeCheckoutDialog({ open, clientSecret, onClose, onComplete }: PortingStripeCheckoutDialogProps) {
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const options = useMemo(() => {
    if (!clientSecret) return null;
    return {
      clientSecret,
      onComplete: () => onCompleteRef.current(),
    };
  }, [clientSecret]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      sx={{ zIndex: "100000 !important" }}
      PaperProps={{
        sx: {
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 800 }}>Pay porting fee</Typography>
          <Typography sx={{ fontSize: 13, color: "text.secondary", fontWeight: 500 }}>
            Card details stay in this window. Closing it returns you to Flow Mobile setup.
          </Typography>
        </Box>
        <IconButton aria-label="Close payment" onClick={onClose}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 1.5, sm: 2.5 }, pb: 2.5 }}>
        {!stripePromise && <Alert severity="warning">Card checkout is not configured.</Alert>}
        {stripePromise && options && (
          <Box sx={{ minHeight: 480 }}>
            <EmbeddedCheckoutProvider key={options.clientSecret} stripe={stripePromise} options={options}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
